import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { addMinutes, differenceInMilliseconds, isSameDay } from 'date-fns';
import { cloneDeep, isEqual } from 'lodash';
import FormFieldInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-interface';
import FormFieldSetInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-set-interface';
import FormFieldValuesInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-values-interface';
import FormInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-interface';
import FormService from '@vertical-plus/vue-js-components/lib/model/services/form/form-service';
import Timesheet from '@/model/entity/audit/timesheet';
import TimesheetDto from '@/model/entity/audit/timesheet-dto';
import TimesheetService from '@/model/service/audit/timesheet-service';

@Component
export default class TimesheetForm extends Vue
{
    @Prop()
    private timesheet!: Timesheet;

    @Prop({
        type: Boolean,
        default: false,
    })
    private saving!: boolean;

    private form: FormInterface|null = null;
    private userInput = false;
    private fieldValues: FormFieldValuesInterface = {};

    private dateToTimeout: number|null = null;
    private userInputTimeout: number|null = null;

    private lastActivity: string|null = null;

    $refs!:
    {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form: any,
    };

    private created()
    {
        this.generateFormFromTimesheet();

        this.dateToTimeout = setTimeout(() =>
        {
            this.updateDateTo();
        }, this.getMsUntilNextMinute() + 50);
    }

    /**
     * Returns the number of milliseconds until the next minute
     *
     * @returns
     */

    private getMsUntilNextMinute(): number
    {
        const now = new Date();
        const nextMinute = this.$clearSeconds(addMinutes(now, 1));

        return differenceInMilliseconds(nextMinute, now);
    }

    private beforeDestroy()
    {
        if (this.dateToTimeout)
        {
            clearTimeout(this.dateToTimeout);
        }

        if (this.userInputTimeout)
        {
            clearTimeout(this.userInputTimeout);
        }
    }

    @Watch('timesheet')
    private onTimesheetChanged()
    {
        if (!this.userInput)
        {
            this.generateFormFromTimesheet();
        }
    }

    @Watch('userInput')
    private onUserInputChanged()
    {
        // Reset userInput after 60 seconds to allow the dateTo to update again
        if (this.userInput)
        {
            if (this.userInputTimeout)
            {
                clearTimeout(this.userInputTimeout);
            }

            this.userInputTimeout = setTimeout(() =>
            {
                this.userInput = false;
            }, 60000);
        }
    }

    /**
     * Updates the date to value
     *
     * @returns
     */

    private updateDateTo()
    {
        if (!this.$refs.form)
        {
            return;
        }

        // Only update if there has been no user input
        if (!this.userInput)
        {
            const now = new Date();

            // Only update if it's the same day as dateFrom
            if (isSameDay(this.fieldValues.dateFrom as Date, now))
            {
                this.$refs.form.setValue('dateTo', this.$clearSeconds(new Date()));
            }
        }

        // Set a new timeout
        this.dateToTimeout = setTimeout(() =>
        {
            this.updateDateTo();
        }, this.getMsUntilNextMinute() + 50);
    }

    /**
     * Handles form field value changes
     *
     * @param fieldValues
     * @returns
     */

    private async onValuesChanged(fieldValues: FormFieldValuesInterface)
    {
        if (!this.form)
        {
            return;
        }

        const originalValues = FormService.getFormValues(this.form);

        if (!isEqual(originalValues, fieldValues))
        {
            this.userInput = true;
        }

        if (!this.$refs.form)
        {
            return;
        }

        // If the type has changed, set the chargeable to n/a
        if (!isEqual(this.fieldValues.type, fieldValues.type))
        {
            if (fieldValues.type === 'other')
            {
                this.$refs.form.setValue('chargeable', 'n/a');
                this.$refs.form.setValue('activity', 'n/a');
            }
            else
            {
                this.$refs.form.setValue('chargeable', null);
                this.$refs.form.setValue('activity', null);
            }
        }

        // If the job has changed, auto-fill the chargeable field based on the type of client
        if (fieldValues.type !== 'other' && !isEqual(this.fieldValues.job, fieldValues.job))
        {
            // Set the chargeable field
            let chargeable: string|null;

            if (fieldValues.job)
            {
                chargeable = await this.getChargeableForJob(fieldValues.job as number);
            }
            else
            {
                chargeable = null;
            }

            this.$refs.form.setValue('chargeable', chargeable);

            // Set the activity field to the last used activity
            this.$refs.form.setValue('activity', this.lastActivity);
        }

        // Store the activity if it has changed
        if (fieldValues.type !== 'other' && fieldValues.job && !isEqual(this.fieldValues.activity, fieldValues.activity))
        {
            this.lastActivity = fieldValues.activity as string;
        }

        this.fieldValues = Object.assign({}, fieldValues);
    }

    /**
     * Returns the most appropriate chargeable for a job
     *
     * @param jobId
     * @returns
     */

    private async getChargeableForJob(jobId: number): Promise<string|null>
    {
        const job = await this.$serviceContainer.jobService.loadJob(jobId);
        if (!job)
        {
            return null;
        }

        // TDS is paid
        if (job.clientId === 933)
        {
            return 'yes';
        }

        // Check for ecommerce
        else if (job.clientId)
        {
            const isEcommerce = await this.$serviceContainer.siteService.clientIsEcommerce(job.clientId);

            // Check if the client is an ecommerce partner
            if (isEcommerce)
            {
                if (job.name.toLocaleLowerCase().includes('adwords'))
                {
                    return 'ecommerce_adwords';
                }
                else
                {
                    return 'ecommerce';
                }
            }

            // Check if V+ ecommerce
            else if (job.client?.name === 'Vertical Plus - Ecommerce')
            {
                return 'ecommerce';
            }

            // Check if V+ unpaid
            else if (job.client?.name.toLocaleLowerCase().includes('vertical plus'))
            {
                return 'no';
            }

            // Assume paid for other clients
            else
            {
                return 'yes';
            }
        }
        else
        {
            return null;
        }
    }

    /**
     * Handles form field double clicks
     *
     * @param field
     * @param component
     * @param event
     */

    private onFieldDoubleClick(
        field: FormFieldInterface,
        component: any,
        event: Event,
    )
    {
        if ([ 'dateFrom', 'dateTo' ].includes(field.key))
        {
            // Get a date rounded to the nearest 5 minutes
            const coeff = 1000 * 60 * 5;
            const date = new Date();
            const rounded = new Date(Math.round(date.getTime() / coeff) * coeff);

            // Set the form value
            this.$refs.form.setValue(field.key, rounded);
        }
    }

    /**
     * Handles form saves
     *
     * @param form
     */

    private async onSave(form: FormInterface)
    {
        if (!this.timesheet)
        {
            return;
        }

        // Generate a timesheet from the form data
        const timesheet = this.generateTimesheetFromForm(form);

        // Emit a save event with the timesheet
        this.$emit('save', timesheet);

        // Reset the user input boolean to allow updates of the base data
        this.userInput = false;

        if (this.userInputTimeout)
        {
            clearTimeout(this.userInputTimeout);
        }
    }

    /**
     * Generates the form from the timesheet
     */

    private generateFormFromTimesheet()
    {
        if (!this.timesheet)
        {
            this.form = null;
            return;
        }

        const form: FormInterface =
        {
            items: [
                {
                    key: 'type',
                    type: 'string',
                    title: this.$t('general.field.type').toString(),
                    value: 'job',
                    required: true,
                    options: [
                        {
                            value: 'job',
                            title: this.$t('general.field.job').toString(),
                        },
                        {
                            value: 'other',
                            title: this.$t('general.field.other').toString(),
                        },
                    ],
                },
                {
                    key: 'job',
                    type: 'integer',
                    title: this.$t('general.field.job').toString(),
                    value: this.timesheet.jobId,
                    required: true,
                    optionsCallback: async(search: string) => this.$serviceContainer.jobService.jobFormOptionsCallback(
                        search,
                        null,
                        false,
                        true,
                    ),
                    titlesCallback: async(values: string[]) => this.$serviceContainer.jobService.jobFormTitlesCallback(values, null),
                    disableInitialOptionsCallback: true,
                    disableEmptySearchResults: true,
                    dependencies: {
                        type: 'job',
                    },
                },
                {
                    key: 'task',
                    type: 'string',
                    title: this.$t('general.field.task').toString(),
                    value: null,
                    required: true,
                    options: [
                        {
                            value: 'lunch',
                            title: this.$t('timesheet.field.lunch').toString(),
                        },
                        {
                            value: 'break',
                            title: this.$t('timesheet.field.break').toString(),
                        },
                    ],
                    dropdown: true,
                    dependencies: {
                        type: 'other',
                    },
                },
                {
                    key: 'dateFrom',
                    type: 'datetime',
                    title: this.$t('general.field.from').toString(),
                    value: this.timesheet.dateFrom,
                    required: true,
                },
                {
                    key: 'dateTo',
                    type: 'datetime',
                    title: this.$t('general.field.to').toString(),
                    value: this.timesheet.dateTo,
                    required: true,
                    validator: (field, fieldValues): string[] =>
                    {
                        const errors: string[] = [];

                        if (field.value && fieldValues.dateFrom && field.value <= fieldValues.dateFrom)
                        {
                            errors.push(this.$t('timesheet.error.must_be_after_from').toString());
                        }

                        return errors;
                    },
                },
                {
                    key: 'chargeable',
                    type: 'string',
                    title: this.$t('timesheet.field.chargeable').toString(),
                    value: null,
                    required: true,
                    options: TimesheetService.chargeableOptions,
                    dropdown: true,
                },
                {
                    key: 'activity',
                    type: 'string',
                    title: this.$t('timesheet.field.activity').toString(),
                    value: null,
                    required: true,
                    options: TimesheetService.activityOptions,
                    dropdown: true,
                },
            ],
        };

        this.form = Object.assign({}, form);
    }

    /**
     * Generates the timesheet from the form
     */

    private generateTimesheetFromForm(form: FormInterface): Timesheet
    {
        if (!this.timesheet)
        {
            return new Timesheet(new TimesheetDto());
        }

        const timesheet = new Timesheet(cloneDeep(this.timesheet.dto));
        timesheet.userId = this.timesheet.userId;

        this.addFieldValues(timesheet, form.items);

        // Set the task value to a string if a job has been set
        if (timesheet.jobId)
        {
            timesheet.task = '';
        }

        return timesheet;
    }

    /**
     * Adds field values from form items to the passed timesheet
     *
     * @param timesheet
     * @param items
     */

    private addFieldValues(timesheet: Timesheet, items: (FormFieldSetInterface | FormFieldInterface)[])
    {
        for (const item of items)
        {
            if (!('key' in item))
            {
                this.addFieldValues(timesheet, item.fields);
                continue;
            }

            switch (item.key)
            {
            case 'job':
                timesheet.jobId = item.value as number;
                break;

            case 'task':
                timesheet.task = item.value as string;
                break;

            case 'dateFrom':
                timesheet.dateFrom = this.$clearSeconds(item.value as Date);
                break;

            case 'dateTo':
                timesheet.dateTo = this.$clearSeconds(item.value as Date);
                break;

            case 'chargeable':
                timesheet.chargeable = item.value as string;
                break;

            case 'activity':
                timesheet.activity = item.value as string;
                break;
            }
        }
    }
}